// Phase 8.WLNAV.v1 — locale-aware formatting.
// Reads the currently active app language from the same localStorage key
// that /lib/i18n/index.js writes ("stockaily_lang"), so the number/currency
// format follows the user's chosen UI language rather than the browser
// default locale. Falls back to "en" when nothing is set.
function _appLocale() {
  try {
    const l = (typeof window !== "undefined" ? window.localStorage.getItem("stockaily_locale") : null) || "en";
    return l === "de" ? "de-DE" : "en-US";
  } catch { return "en-US"; }
}

export function fmtPrice(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "–";
  const v = Number(n);
  return v.toLocaleString(_appLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function fmtCurrency(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "–";
  const v = Number(n);
  // Use Intl currency style so "$" placement and grouping match locale:
  //   en-US: $1,234.56
  //   de-DE: 1.234,56 $
  try {
    return new Intl.NumberFormat(_appLocale(), {
      style: "currency", currency: "USD",
      minimumFractionDigits: 2, maximumFractionDigits: 2,
      currencyDisplay: "narrowSymbol",
    }).format(v);
  } catch {
    return `$${v.toLocaleString(_appLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
}

export function fmtCompact(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "–";
  const v = Number(n);
  const abs = Math.abs(v);
  if (abs >= 1e12) return `$${(v / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `$${(v / 1e3).toFixed(2)}K`;
  return `$${v.toFixed(2)}`;
}

// Correction Block 2 (finding #6) — Currency-aware monetary formatters.
// Attach a "$" only when the reporting currency is verifiably USD.
// When the value's currency basis cannot be reliably established
// (i.e. ``valuation_unit_status`` on the enclosing analysis is not
// "reconciled"), we render the monetary field as unavailable rather
// than guessing a currency symbol.  Ratio / percentage metrics (P/E,
// margins, growth, etc.) are unit-free and stay visible.
//
//   unitStatus: string | null | undefined
//     null / undefined / "reconciled" → treated as USD-verified,
//     preserving the historical rendering for US issuers.
//     Any other value (e.g. "unreconciled_currency",
//     "unreconciled_share_basis", "source_unavailable") → the value
//     is suppressed because we cannot safely label it.
function _isReconciled(unitStatus) {
  if (unitStatus === null || unitStatus === undefined || unitStatus === "") return true;
  return String(unitStatus).toLowerCase() === "reconciled";
}

export function fmtMoneyCompact(n, unitStatus, reportCurrency) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "–";
  if (!_isReconciled(unitStatus)) return "–";
  const ccy = String(reportCurrency || "USD").toUpperCase();
  const v = Number(n);
  const abs = Math.abs(v);
  const suffix = abs >= 1e12
    ? { d: (v / 1e12).toFixed(2), u: "T" }
    : abs >= 1e9
    ? { d: (v / 1e9).toFixed(2), u: "B" }
    : abs >= 1e6
    ? { d: (v / 1e6).toFixed(2), u: "M" }
    : abs >= 1e3
    ? { d: (v / 1e3).toFixed(2), u: "K" }
    : { d: v.toFixed(2), u: "" };
  if (ccy === "USD") return `$${suffix.d}${suffix.u}`;
  return `${suffix.d}${suffix.u} ${ccy}`;
}

export function fmtMoney(n, unitStatus, reportCurrency) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "–";
  if (!_isReconciled(unitStatus)) return "–";
  const ccy = String(reportCurrency || "USD").toUpperCase();
  const v = Number(n);
  const body = v.toLocaleString(_appLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (ccy === "USD") return `$${body}`;
  return `${body} ${ccy}`;
}

export function fmtPercent(n, digits = 1) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "–";
  return `${Number(n).toLocaleString(_appLocale(), { minimumFractionDigits: digits, maximumFractionDigits: digits })}%`;
}

export function fmtPercentSigned(n, digits = 1) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "–";
  const v = Number(n);
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toLocaleString(_appLocale(), { minimumFractionDigits: digits, maximumFractionDigits: digits })}%`;
}

export function fmtRatio(n, digits = 2) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "–";
  return Number(n).toFixed(digits);
}

export function fmtMultiplier(n, digits = 1) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "–";
  return `${Number(n).toFixed(digits)}x`;
}


/**
 * Phase 6.AI — Universal safe number formatter.
 *
 * Use this EVERYWHERE in analyze-page rendering instead of raw
 * `.toFixed()` to eliminate "Cannot read properties of null (reading
 * 'toFixed')" crashes when a backend metric is null/undefined/NaN.
 *
 * @param {*} n        Value of any type (typically number | null | undefined)
 * @param {number} digits  Decimal places (default 1)
 * @param {string} fallback  Returned when n is not a finite number (default "—")
 * @returns {string}   Formatted number or fallback
 *
 * Examples:
 *   safeFixed(null)          → "—"
 *   safeFixed(undefined, 2)  → "—"
 *   safeFixed(NaN)           → "—"
 *   safeFixed("abc")         → "—"
 *   safeFixed(0)             → "0.0"
 *   safeFixed(42.567, 2)     → "42.57"
 */
export function safeFixed(n, digits = 1, fallback = "—") {
  if (n === null || n === undefined) return fallback;
  const v = Number(n);
  if (!Number.isFinite(v)) return fallback;
  return v.toFixed(digits);
}

/**
 * Convenience: format a 0..1 ratio as a 0..100% string with safe fallback.
 *   safeRatioPct(0.65) → "65%"
 *   safeRatioPct(null) → "—"
 */
export function safeRatioPct(n, digits = 0, fallback = "—") {
  if (n === null || n === undefined) return fallback;
  const v = Number(n);
  if (!Number.isFinite(v)) return fallback;
  return `${(v * 100).toFixed(digits)}%`;
}

/**
 * Convenience: format a score (0..100) with safe fallback.
 *   safeScore(72)   → "72"
 *   safeScore(null) → "—"
 */
export function safeScore(n, digits = 0, fallback = "—") {
  return safeFixed(n, digits, fallback);
}
